import { useGetAllDashboardsInfo, useGetDashboard } from "api/api-hooks/dashboards-hooks";
import { T } from "services/lang/LanguageService";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DashboardTabModel, OldDashboardTabModel } from "api/model/dashboard/DashboardTab";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useState } from "react";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import {
  DashboardLayoutType,
  IDashboardComponent,
  OldDashboardComponent,
} from "api/model/dashboard/DashboardComponent";
import { getDefaultLayout } from "./layouts/helpers";

interface ImportDashboardProps {
  dashboard: Dashboard;
  isImportDashboardDialogVisible: boolean;
  setIsImportDashboardDialogVisibility: (isImportDashboardDialogVisible: boolean) => void;
}

export const handleAddNewTab = (
  dashboard: Dashboard,
  setDashboard: any,
  tabTitle?: string,
  componentsToAdd?: OldDashboardComponent[],
  componentGroups?: DashboardComponentGroup[]
) => {
  const updatedCurrentDashboard = {
    ...dashboard,
    dashboardTabsJs: [
      ...dashboard.dashboardTabsJs,
      {
        uuid: uuidv4(),
        title: tabTitle ?? T("word.new-tab"),
        componentGroups: componentGroups ?? [
          {
            id: uuidv4(),
            layout: {
              type: DashboardLayoutType.HORIZONTAL_FLEX,
            },
            components: componentsToAdd,
          },
        ],
      },
    ],
  };
  setDashboard(updatedCurrentDashboard);
};

const ImportDashboardOrTab = (props: ImportDashboardProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const [selectedDashboardId, setSelectedDashboardId] = useState<number | null>(null);
  const [selectedDashboardTabId, setSelectedDashboardTabId] = useState<number | null>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const { data: dashboards } = useGetAllDashboardsInfo();
  const { data: dashboardData } = useGetDashboard(
    selectedDashboardId !== null ? selectedDashboardId : 0,
    {
      enabled: selectedDashboardId !== null,
    }
  );

  const dashboardOptions =
    dashboards?.map(currentDashboard => ({
      label: currentDashboard.title,
      value: currentDashboard.id,
    })) || [];

  const tabOptions =
    dashboardData?.dashboardTabs && dashboardData?.dashboardTabs?.length > 0
      ? dashboardData?.dashboardTabs.map((tab, index) => ({
          label: tab.title,
          value: index,
        })) || []
      : dashboardData?.dashboardTabsJs.map((tab, index) => ({
          label: tab.title,
          value: index,
        })) || [];

  const importNewDashboard = (newDashboard: Dashboard) => {
    if (newDashboard.dashboardTabsJs.length > 0) {
      setDashboard(newDashboard);
    } else {
      const newTabs = newDashboard.dashboardTabs.map(tab => {
        const components: IDashboardComponent[] = tab.components.map(
          (component: OldDashboardComponent) => {
            if (component.TYPE !== "ADVANCED_COMPONENT" && component.TYPE !== "SEPARATOR") {
              return {
                id: uuidv4(),
                title: T("label.not-supported"),
                type: "NOT_SUPPORTED",
                config: {},
                layout: {
                  HORIZONTAL_FLEX: {
                    width: 12,
                    height: 100,
                  },
                },
                defaultCollapsed: false
              };
            }

            const currentLayout = JSON.parse(
              JSON.stringify(getDefaultLayout(DashboardLayoutType.HORIZONTAL_FLEX))
            );

            if (currentLayout && currentLayout.HORIZONTAL_FLEX) {
              currentLayout.HORIZONTAL_FLEX.width =
                component.width || currentLayout.HORIZONTAL_FLEX.width;
            }

            return {
              id: uuidv4(),
              title: component.title || "",
              type: component.componentType,
              config: component.jsonConfig ? JSON.parse(component.jsonConfig) : {},
              layout: currentLayout,
              defaultCollapsed: false
            };
          }
        );

        return {
          uuid: tab.uuid || uuidv4(),
          title: tab.title || T("word.new-tab"),
          componentGroups: [
            {
              id: uuidv4(),
              layout: {
                type: DashboardLayoutType.HORIZONTAL_FLEX,
              },
              components,
            },
          ],
        };
      });

      const updatedCurrentDashboard = {
        ...dashboard,
        dashboardTabsJs: newTabs,
      } as Dashboard;
      setDashboard(updatedCurrentDashboard);
    }
  };

  const handleDashboardChange = (e: DropdownChangeEvent) => {
    setSelectedDashboardId(e.value);
    setSelectedDashboardTabId(null);
  };

  const handleTabChange = (e: DropdownChangeEvent) => {
    setSelectedDashboardTabId(e.value);
  };

  const handleImportDashboard = () => {
    if (dashboardData && selectedTabIndex === 0) {
      importNewDashboard(dashboardData);
    } else if (dashboardData && selectedTabIndex === 1 && selectedDashboardTabId !== null) {
      const selectedTab = dashboardData.dashboardTabs[selectedDashboardTabId];

      const newDashboard: Dashboard = {
        ...dashboardData,
        dashboardTabs: [selectedTab],
      };
      importNewDashboard(newDashboard);
    }
    props.setIsImportDashboardDialogVisibility(false);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  function isOldDashboardTabModel(
    tab: OldDashboardTabModel | DashboardTabModel
  ): tab is OldDashboardTabModel {
    return (tab as OldDashboardTabModel).components !== undefined;
  }

  const handleImportTab = () => {
    if (dashboardData && selectedTabIndex === 1 && selectedDashboardTabId !== null) {
      const selectedTab =
        dashboardData.dashboardTabs.length > 0
          ? dashboardData.dashboardTabs[selectedDashboardTabId]
          : dashboardData.dashboardTabsJs[selectedDashboardTabId];

      if (isOldDashboardTabModel(selectedTab)) {
        const componentsToAdd = selectedTab.components.map((component: OldDashboardComponent) => ({
          id: uuidv4(),
          title: component.title,
          type: component.componentType,
          config: component.jsonConfig ? JSON.parse(component.jsonConfig) : {},
          layout: {
            HORIZONTAL_FLEX: {
              width: component.width || 12,
              height: "auto",
            },
          },
          TYPE: component.TYPE,
          width: component.width,
          componentType: component.componentType,
          jsonConfig: component.jsonConfig,
        }));

        handleAddNewTab(props.dashboard, setDashboard, selectedTab.title, componentsToAdd);
      } else {
        handleAddNewTab(
          props.dashboard,
          setDashboard,
          selectedTab.title,
          [],
          selectedTab.componentGroups
        );
      }
    }

    props.setIsImportDashboardDialogVisibility(false);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  const handleTabChangeView = (e: any) => {
    setSelectedTabIndex(e.index);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  const displayFooter = (
    <div>
      <Button
        label={T("action.import")}
        icon="pi pi-check"
        onClick={selectedTabIndex === 0 ? handleImportDashboard : handleImportTab}
        disabled={
          (selectedTabIndex === 0 && selectedDashboardId === null) ||
          (selectedTabIndex === 1 && selectedDashboardTabId === null)
        }
      />
    </div>
  );

  return (
    <Dialog
      header={T("action.import")}
      visible={props.isImportDashboardDialogVisible}
      onHide={() => props.setIsImportDashboardDialogVisibility(false)}
      footer={displayFooter}
      style={{ width: "25rem", maxHeight: "30rem" }}
      blockScroll
    >
      <TabView activeIndex={selectedTabIndex} onTabChange={handleTabChangeView}>
        <TabPanel header={T("word.dashboard")}>
          <div className="w-full flex flex-column gap-3">
            <label htmlFor="dashboard">{T("action.select-dashboard")}</label>
            <Dropdown
              id="dashboard"
              value={selectedDashboardId}
              options={dashboardOptions}
              filter
              onChange={handleDashboardChange}
              optionLabel="label"
              optionValue="value"
              placeholder={T("action.select-dashboard")}
            />
          </div>
        </TabPanel>
        <TabPanel header={T("word.tab")}>
          <div className="w-full flex flex-column gap-3">
            <label htmlFor="dashboard">{T("action.select-dashboard")}</label>
            <Dropdown
              id="dashboard"
              value={selectedDashboardId}
              options={dashboardOptions}
              filter
              onChange={handleDashboardChange}
              optionLabel="label"
              optionValue="value"
              placeholder={T("action.select-dashboard")}
            />
          </div>
          {selectedDashboardId !== null && (
            <div className="w-full flex flex-column gap-3 mt-3">
              <label htmlFor="tab">{T("action.select-tab")}</label>
              <Dropdown
                id="tab"
                value={selectedDashboardTabId}
                options={tabOptions}
                filter
                onChange={handleTabChange}
                optionLabel="label"
                optionValue="value"
                placeholder={T("action.select-tab")}
                disabled={tabOptions.length === 0}
              />
            </div>
          )}
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default ImportDashboardOrTab;
